import React from "react";
import { useShallow } from "zustand/react/shallow";
import { useNavigate, useParams } from "react-router-dom";
import { Event, RoomState } from "@uno/protocol";
import { useTranslation } from "react-i18next";
import { AppContext } from "context";
import Container from "component/Layout/Container";
import ButtonHapticWrapper from "component/Button/ButtonHapticWrapper";
import Popover from "component/Popover";
import LongPressButton from "component/Button/LongPressButton";
import CountdownBadge from "./components/CountdownBadge";
import RoomPlayersContainer from "./components/RoomPlayersContainer";
import useStoreRoom from "store/useStoreRoom";
import useStoreRoomSession from "store/useStoreRoomSession";
import Discard from "./components/Discard";
import Deck from "./components/Deck";
import SwapHandsOverlay from "./components/SwapHandsOverlay";
import useRoomEvent from "hook/room/useRoomEvent";
import useRoomEmoteListener from "hook/room/useRoomEmoteListener";
import useRoomChatListener from "hook/room/useRoomChatListener";
import ChatDrawer from "./components/Chat/ChatStream";
import useStoreRoomChat from "store/useStoreRoomChat";
import hostIntegration from "../../integrations/host";
import { RoomLayoutProvider, useRoomLayout } from "context/RoomLayoutContext";
import Ranking from "./components/Ranking";
import "./index.scss";
import "css/cards-v2.scss";

const RoomEvents = () => {
    useRoomEvent();
    useRoomEmoteListener();
    useRoomChatListener();

    return null;
};

const RoomRanking = () => {
    const room = useStoreRoom(state => state.room);

    if(room?.state !== RoomState.Ended) {
        return null;
    }

    return <Ranking room={room} />;
};

const RoomBoard = React.memo(function RoomBoard({ players }: { players?: number }) {
    const layout = useRoomLayout();
    const roomStyle: React.CSSProperties & Record<string, string | number> = {
        "--room-scale": layout.scale,
        "--room-board-width": `${layout.board.width}px`,
        "--room-board-height": `${layout.board.height}px`,
        "--room-board-top": `${layout.board.top}px`,
        "--room-discard-size": `${layout.discardSize}px`,
    };

    return (
        <div
            id="room"
            data-layout={layout.orientation}
            data-board-shape={layout.boardShape}
            data-tooltip-placement={layout.actionTooltipPlacement}
            className="absolute inset-0 mx-auto w-dvw h-dvh max-w-4xl flex justify-center"
            style={roomStyle}
        >
            <div
                id="board"
                data-players={players}
                className="relative"
            >
                <div className="board-ring absolute rounded-full" style={{ inset: "-1px" }}></div>

                <RoomPlayersContainer />
                <Discard />
                <Deck />
                <SwapHandsOverlay />
            </div>
        </div>
    );
});

export const Room = () => {
    const { roomId } = useParams<{ roomId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { socket } = React.useContext(AppContext);
    const room = useStoreRoom(useShallow(state => ({
        id: state.room?.id,
        state: state.room?.state,
        playersCount: state.room?.players.length,
        endsAt: state.room?.gameState.endsAt ?? 0,
    })));
    const [showLeaveConfirm, setShowLeaveConfirm] = React.useState(false);

    const handleLeaveRoom = React.useCallback(() => {
        if(socket?.connected && roomId) {
            socket.emit(Event.PlayerLeaveRoom, roomId);
        }

        navigate("/");
    }, [socket, roomId, navigate]);

    React.useEffect(() => {
        if(roomId) {
            useStoreRoomSession.getState().init(roomId);
        }
    }, [roomId]);

    React.useEffect(() => {
        if(!socket || !roomId) {
            return;
        }

        const onConnect = () => {
            useStoreRoomSession.getState().init(roomId);
            socket.emit(Event.RoomPlayerBackInGame, roomId);
        };

        if(socket.connected) {
            onConnect();
        }

        socket.on("connect", onConnect);

        return () => {
            socket.off("connect", onConnect);
        };
    }, [socket, roomId]);

    React.useEffect(() => {
        return () => {
            useStoreRoomChat.getState().reset();
        };
    }, [roomId]);

    React.useEffect(() => {
        if(room.id && room.id === roomId) {
            hostIntegration.syncRoom({
                id: room.id,
                joinable: false,
            });
        }
    }, [room.id, roomId]);

    React.useEffect(() => {
        return () => hostIntegration.syncRoom(null);
    }, [roomId]);

    React.useEffect(() => {
        if(!socket || !roomId) {
            return;
        }

        if(!socket.connected) {
            navigate("/", { replace: true });
            return;
        }

        socket.emit(Event.PlayerCheckRoom, roomId, (active: boolean) => {
            if(!active) {
                navigate("/", { replace: true });
            }
        });
    }, [socket, roomId]);

    const headerLeft = (
        <>
            <Popover
                visible={showLeaveConfirm}
                onClose={() => setShowLeaveConfirm(false)}
                placement="bottom"
                content={
                    <div className="flex flex-col gap-3 w-[220px] p-3 rounded-xl border border-white/10" style={{ background: "rgba(17,24,39,0.95)" }}>
                        <p className="text-white/40 text-[11px] leading-tight">{t("room.leave_warning")}</p>
                        <LongPressButton
                            onConfirm={handleLeaveRoom}
                            className="w-full py-2.5 rounded-lg text-white text-xs font-semibold bg-red-500 cursor-pointer"
                        >
                            {t("room.leave_confirm")}
                        </LongPressButton>
                    </div>
                }
            >
                <ButtonHapticWrapper>
                    <button onClick={() => setShowLeaveConfirm(true)} className="flex items-center hover:opacity-60 transition-opacity cursor-pointer">
                        <svg className="w-6 h-6 md:w-7 md:h-7 text-white drop-shadow-md" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                </ButtonHapticWrapper>
            </Popover>
            {room.state === RoomState.Playing && (
                <CountdownBadge endsAt={room.endsAt} />
            )}
        </>
    );

    return (
        <>
            <RoomEvents />

            {(!room.id || room.id === roomId) && (
                <RoomLayoutProvider>
                    <Container id="room-wrapper" headerLeft={headerLeft} hideFooter>
                        <RoomBoard players={room.playersCount} />

                        <ChatDrawer />

                        <RoomRanking />
                    </Container>
                </RoomLayoutProvider>
            )}
        </>
    );
};

export default Room;
